import { Plus, Trash2 } from 'lucide-react';
import { Botao, Campo, Entrada } from '../componentes/ui';
import { centavosDeReais, moedaBR, type RascunhoSociedade } from './sociedade-nova-comum';
import { LinhaCheia, Secao } from './sociedade-nova-secao';

/**
 * As FILIAIS — no cadastro e na alteração, pelo mesmo componente.
 *
 * ## Por que ela vive junto do resto do formulário
 * Abrir ou fechar filial é ALTERAÇÃO CONTRATUAL: o contador muda o cadastro e
 * o servidor descobre o que mudou. Uma tela separada de "gerenciar filiais"
 * criaria um segundo caminho para mexer na sociedade — com uma segunda trilha
 * de atos —, e o dia em que os dois divergissem o contrato consolidado não
 * bateria com a realidade registrada.
 *
 * ## Encerrar não é apagar
 * A lixeira remove a linha do formulário, e o servidor lê isso como
 * encerramento — mesma regra da saída de sócio. A filial fica no histórico do
 * ato que a fechou; o que sai é a descrição no contrato vigente.
 */
export function SecaoFiliais({
  rascunho: r,
  mudar,
}: {
  rascunho: RascunhoSociedade;
  mudar: (p: Partial<RascunhoSociedade>) => void;
}): JSX.Element {
  const filiais = r.filiais ?? [];

  const mexer = (i: number, parcial: Partial<(typeof filiais)[number]>) =>
    mudar({ filiais: filiais.map((f, j) => (j === i ? { ...f, ...parcial } : f)) });

  const totalDestacado = filiais.reduce(
    (t, f) => t + centavosDeReais(f.capitalDestacado),
    0,
  );

  return (
    <Secao
      titulo="Filiais"
      descricao="Estabelecimentos secundários. A esmagadora maioria das sociedades não tem nenhum."
    >
      <LinhaCheia>
        <div className="flex flex-col gap-3">
          {filiais.length === 0 && (
            <p className="text-tinta-500 text-sm">
              Nenhuma filial. Abrir uma depois é uma alteração contratual.
            </p>
          )}

          {filiais.map((f, i) => (
            <div
              key={i}
              className="border-tinta-100 flex flex-col gap-3 rounded-lg border p-3"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr_1fr_auto]">
                <Campo
                  rotulo={`Logradouro da filial ${String(i + 1)}`}
                  id={`fil-log-${String(i)}`}
                  obrigatorio
                >
                  <Entrada
                    id={`fil-log-${String(i)}`}
                    value={f.logradouro}
                    onChange={(e) => mexer(i, { logradouro: e.target.value })}
                  />
                </Campo>

                <Campo rotulo="Número" id={`fil-num-${String(i)}`} obrigatorio>
                  <Entrada
                    id={`fil-num-${String(i)}`}
                    value={f.numero}
                    onChange={(e) => mexer(i, { numero: e.target.value })}
                  />
                </Campo>

                <Campo rotulo="Bairro" id={`fil-bai-${String(i)}`} obrigatorio>
                  <Entrada
                    id={`fil-bai-${String(i)}`}
                    value={f.bairro}
                    onChange={(e) => mexer(i, { bairro: e.target.value })}
                  />
                </Campo>

                <Botao
                  variante="fantasma"
                  tamanho="icone"
                  aria-label={`Encerrar filial ${String(i + 1)}`}
                  title="Encerrar esta filial — ela sai do contrato e fica no histórico"
                  onClick={() => mudar({ filiais: filiais.filter((_, j) => j !== i) })}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Botao>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                <Campo rotulo="Município" id={`fil-mun-${String(i)}`} obrigatorio>
                  <Entrada
                    id={`fil-mun-${String(i)}`}
                    value={f.municipio}
                    onChange={(e) => mexer(i, { municipio: e.target.value })}
                  />
                </Campo>

                <Campo rotulo="UF" id={`fil-uf-${String(i)}`} obrigatorio>
                  <Entrada
                    id={`fil-uf-${String(i)}`}
                    maxLength={2}
                    value={f.uf}
                    onChange={(e) => mexer(i, { uf: e.target.value.toUpperCase() })}
                  />
                </Campo>

                <Campo
                  rotulo="CEP"
                  id={`fil-cep-${String(i)}`}
                  obrigatorio
                  dica="Só números"
                >
                  <Entrada
                    id={`fil-cep-${String(i)}`}
                    inputMode="numeric"
                    maxLength={8}
                    value={f.cep}
                    onChange={(e) => mexer(i, { cep: e.target.value.replace(/\D/g, '') })}
                  />
                </Campo>

                <Campo
                  rotulo="Abertura"
                  id={`fil-ab-${String(i)}`}
                  obrigatorio
                  dica="Data do ato que a abriu."
                >
                  <Entrada
                    id={`fil-ab-${String(i)}`}
                    type="date"
                    value={f.abertura}
                    onChange={(e) => mexer(i, { abertura: e.target.value })}
                  />
                </Campo>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Campo
                  rotulo="CNPJ da filial"
                  id={`fil-cnpj-${String(i)}`}
                  dica="Vazio até a Junta emitir. A raiz é a mesma da matriz."
                >
                  <Entrada
                    id={`fil-cnpj-${String(i)}`}
                    inputMode="numeric"
                    maxLength={14}
                    value={f.cnpj}
                    onChange={(e) =>
                      mexer(i, { cnpj: e.target.value.replace(/\D/g, '') })
                    }
                  />
                </Campo>

                <Campo
                  rotulo="Capital destacado (R$)"
                  id={`fil-cap-${String(i)}`}
                  dica="Vazio = sem destaque, que é o caso comum."
                >
                  <Entrada
                    id={`fil-cap-${String(i)}`}
                    inputMode="decimal"
                    value={f.capitalDestacado}
                    onChange={(e) => mexer(i, { capitalDestacado: e.target.value })}
                  />
                </Campo>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-3">
            <Botao
              variante="secundaria"
              onClick={() =>
                mudar({
                  filiais: [
                    ...filiais,
                    {
                      logradouro: '',
                      numero: '',
                      bairro: '',
                      municipio: '',
                      uf: r.sede.uf,
                      cep: '',
                      cnpj: '',
                      capitalDestacado: '',
                      abertura: r.inicioAtividades,
                    },
                  ],
                })
              }
            >
              <Plus className="size-4" aria-hidden />
              Adicionar filial
            </Botao>

            {/*
              O total destacado ao lado, porque o validador recusa quando ele
              passa do capital social — e descobrir isso só no laudo é o
              caminho mais longo até a informação.
            */}
            {totalDestacado > 0 && (
              <span className="text-tinta-500 text-sm tabular-nums">
                Capital destacado: {moedaBR(totalDestacado)}
              </span>
            )}
          </div>
        </div>
      </LinhaCheia>
    </Secao>
  );
}
